/**
 * Client-Side Tenant Detection Hook
 *
 * Detects tenant mode from hostname to support multi-tenant routing.
 */

import { useMemo } from "react";

export type TenantMode =
  | "subdomain"
  | "custom_domain"
  | "path"
  | "admin"
  | "dev";

export interface TenantInfo {
  mode: TenantMode;
  orgSlug: string | null;
  isCustomDomain: boolean;
  isAdmin: boolean;
}

// Base domain from env (default for dev)
const BASE_DOMAIN = import.meta.env.VITE_BASE_DOMAIN || "wondermaps.co";

/**
 * Detect tenant info from current hostname.
 * Runs on client only (uses window.location).
 */
export function detectTenant(): TenantInfo {
  const hostname = window.location.hostname.toLowerCase();
  const pathname = window.location.pathname;

  // Localhost = dev mode
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    // Check if pathname suggests admin
    const isAdmin = pathname.includes("/admin");
    return {
      mode: "dev",
      orgSlug: null,
      isCustomDomain: false,
      isAdmin,
    };
  }

  // Admin subdomain: app.{baseDomain}
  if (hostname === `app.${BASE_DOMAIN}`) {
    return {
      mode: "admin",
      orgSlug: null,
      isCustomDomain: false,
      isAdmin: true,
    };
  }

  // Check if subdomain of base domain
  if (hostname.endsWith(`.${BASE_DOMAIN}`)) {
    const subdomain = hostname.slice(0, -(BASE_DOMAIN.length + 1));

    // Reject multi-level subdomains
    if (subdomain.includes(".")) {
      return {
        mode: "admin",
        orgSlug: null,
        isCustomDomain: false,
        isAdmin: true,
      };
    }

    return {
      mode: "subdomain",
      orgSlug: subdomain,
      isCustomDomain: false,
      isAdmin: false,
    };
  }

  // Exact match on base domain (no subdomain)
  if (hostname === BASE_DOMAIN) {
    return {
      mode: "admin",
      orgSlug: null,
      isCustomDomain: false,
      isAdmin: true,
    };
  }

  // Any other domain = custom domain
  return {
    mode: "custom_domain",
    orgSlug: null,
    isCustomDomain: true,
    isAdmin: false,
  };
}

/**
 * React hook for tenant detection.
 * Returns memoized tenant info based on current hostname.
 */
export function useTenant(): TenantInfo {
  return useMemo(() => detectTenant(), []);
}
